import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {change} from 'redux-form'
import {required} from '@cred/shared/validations'
import Alert from '@cred/react-alert'
import rollbar from '@cred/shared/rollbar'
import loadGoogleMapsApi from 'load-google-maps-api'
import config from 'config'
import {
  Input,
  Dropdown,
  AddressSearch,
  textInputWithAnalytics as textInput,
  selectWithAnalytics as select,
  zipInputWithAnalytics as zipInput
} from '../components'
import withForm from '@cred/shared/hoc/with-form'
import {states} from './states'
import styles from './styles.module.scss'

/**
 * @visibleName Multiline Address
 */
class Address extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    autoFocus: PropTypes.bool,
    focusAfter: PropTypes.number,
    className: PropTypes.string,
    loadingComponent: PropTypes.element,
    formName: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired
  }

  static defaultProps = {
    className: '',
    autoFocus: false,
    focusAfter: 500,
    loadingComponent: null
  }

  state = {
    googleMapsLoaded: false,
    showAlert: false,
    hideSuggestList: false
  }

  _addressSearchInput = ''

  async componentDidMount() {
    try {
      await loadGoogleMapsApi({
        key: config.googleMapsApiKey,
        libraries: ['places']
      })

      this.setState({googleMapsLoaded: true})
    } catch (error) {
      rollbar.error('Error loading Google Maps Api', error)
    }
  }

  skipEverySuggest = () => true

  getSuggestLabel = suggest => suggest.structured_formatting.main_text

  renderSuggestItem = suggest => suggest.description

  render() {
    const {name, className, loadingComponent, autoFocus, focusAfter} = this.props,
      {googleMapsLoaded, showAlert, hideSuggestList} = this.state

    return (
      <div className={className}>
        {showAlert && <Alert dismissible className="mb-16">
          Could not find your full address.<br/>Please enter details manually
        </Alert>}
        <div className={styles.line}>
          {googleMapsLoaded ?
            <AddressSearch
              autoFocus={autoFocus}
              autoFocusDelay={focusAfter}
              autoComplete="section-address street-address address-line1"
              name={`${name}.street`}
              label="Street Address"
              placeholder="Street Address"
              validate={required}
              onChange={this.handleChangeAddress}
              onKeyDown={this.handleKeyDown}
              onSelectAddress={this.handleSelectAddress}
              skipSuggest={hideSuggestList ? this.skipEverySuggest : undefined}
              getSuggestLabel={this.getSuggestLabel}
              renderSuggestItem={this.renderSuggestItem}
            />
            :
            <div>
              <Input
                type="hidden"
                name={`${name}.street`}
                validate={required}
                className="inspectletIgnore"
              />
              {loadingComponent}
            </div>
          }
        </div>
        <div className={styles.line}>
          <Input
            type="text"
            label="Unit (Optional)"
            placeholder="Unit (Optional)"
            autoComplete="section-address street-address address-line2"
            name={`${name}.unit`}
            className="mt-16 inspectletIgnore"
            component={textInput}
          />
        </div>
        <div className={styles.line}>
          <Input
            type="text"
            label="City"
            name={`${name}.city`}
            placeholder="City"
            autoComplete="section-address address-level2"
            className="mt-16 inspectletIgnore"
            validate={required}
            component={textInput}
          />
        </div>
        <div className={styles.line}>
          <Dropdown
            id="state"
            name={`${name}.state`}
            label="State"
            placeholder="State"
            autoComplete="section-address address-level1"
            className="mt-16 inspectletIgnore"
            options={states}
            component={select}
          />
        </div>
        <div className={styles.line}>
          <Input
            type="tel"
            label="Zip Code"
            name={`${name}.zipcode`}
            placeholder="Zip Code"
            autoComplete="section-address postal-code"
            className="mt-16 inspectletIgnore"
            validate={required}
            component={zipInput}
          />
        </div>
      </div>
    )
  }

  handleSelectAddress = ({street, city, state, zipcode, complete}) => {
    const {name, formName, dispatch} = this.props

    dispatch(change(formName, `${name}.street`, street))
    dispatch(change(formName, `${name}.city`, city))
    dispatch(change(formName, `${name}.state`, state))
    dispatch(change(formName, `${name}.zipcode`, zipcode))

    if (!complete) {
      this.setState({showAlert: true})
    }
  }

  handleChangeAddress = (e, value) => {
    /**
     * We are updating {this._addressSearchInput} on every key down.
     * If at some point of time change event was fired with a different value,
     * we assume that input was filled in via browser's auto-fill feature.
     */
    if (this._addressSearchInput !== value) {
      this._addressSearchInput = value
      this.setState({hideSuggestList: true})
    }
  }

  handleKeyDown = ({key, keyCode}) => {
    // Code {8} represents a backspace key
    if (keyCode === 8) {
      this._addressSearchInput = this._addressSearchInput.slice(0, -1)

      // If either backspace ...
      this.setState({hideSuggestList: false})
    } else if (keyCode >= 48 && keyCode <= 90) { // All codes starting from {48} and up to {90} represent symbols
      this._addressSearchInput += key

      // Or any symbolic key was pressed we need to enable Geosuggest list
      this.setState({hideSuggestList: false})
    }
  }
}

export {Address as AddressNoHoc}
export default /*#__PURE__*/ withForm(Address)
